// hooks/ga4/useGA4ActiveUsersMock.js
import { useMemo, useState } from 'react';
import { getMockGA4ActiveUsers } from '../../mock_data/mock_data/mockgscdata';

/**
 * Mock Version of GA4 Active Users Hook
 * Uses local mock data instead of API calls - perfect for demos!
 * 
 * @param {number} months - Number of months to show (default 6)
 * @param {boolean} includeCurrentMonth - Whether to include current month (default true)
 */
export const useGA4ActiveUsers = (months = 6, includeCurrentMonth = true) => {
  const [isRefetching, setIsRefetching] = useState(false);
  const [dataUpdatedAt, setDataUpdatedAt] = useState(Date.now());

  // Simulate data fetching with mock data
  const apiData = useMemo(() => {
    return getMockGA4ActiveUsers();
  }, []);

  // Mock refetch function that simulates a refresh
  const refetch = async () => {
    setIsRefetching(true);
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setDataUpdatedAt(Date.now());
    setIsRefetching(false);
    
    return { data: apiData };
  };

  // Transform and group channels (same logic as real hook)
  const transformedData = useMemo(() => {
    if (!apiData) {
      return {
        chartData: [],
        channelStats: [],
        totals: [],
        metadata: null,
        topPerformers: null,
      };
    }

    const { periods, channels, totals, channel_stats, metadata } = apiData;

    // Channel grouping logic (from original hook)
    const groupChannel = (channelName) => {
      const name = channelName.toLowerCase();
      
      if (name.includes('direct')) return 'Direct';
      if (name.includes('organic')) return 'Organic';
      if (name.includes('email')) return 'Email';
      return 'Other';
    };

    // Group channels by category
    const groupedChannels = {};
    
    Object.entries(channels).forEach(([channelName, values]) => {
      const group = groupChannel(channelName);
      
      if (!groupedChannels[group]) {
        groupedChannels[group] = Array(values.length).fill(0);
      }
      
      // Sum values into the group
      values.forEach((value, index) => {
        groupedChannels[group][index] += value;
      });
    });

    // Build chart data format for Recharts
    const chartData = periods.map((period, index) => {
      const dataPoint = {
        period,
        Direct: groupedChannels['Direct']?.[index] || 0,
        Organic: groupedChannels['Organic']?.[index] || 0,
        Email: groupedChannels['Email']?.[index] || 0,
        Other: groupedChannels['Other']?.[index] || 0,
        total: totals[index] || 0,
      };
      return dataPoint;
    });

    // Calculate grouped channel statistics
    const groupedStats = Object.entries(groupedChannels).map(([group, values]) => {
      const total = values.reduce((sum, val) => sum + val, 0);
      const avg = total / values.length;
      const grandTotal = totals.reduce((sum, val) => sum + val, 0);
      const percentage = grandTotal > 0 ? (total / grandTotal) * 100 : 0;

      return {
        channel: group,
        total_users: total,
        avg_users_per_month: Math.round(avg * 100) / 100,
        percentage_of_total: Math.round(percentage * 100) / 100,
      };
    });

    // Sort by total users descending
    groupedStats.sort((a, b) => b.total_users - a.total_users);
    
    // Helper function to calculate which channel had most growth
    const calculateMostGrowth = (groupedChannels, periods) => {
      if (!groupedChannels || periods.length < 2) return null;

      let maxGrowth = -Infinity;
      let topGrowthChannel = null;

      Object.entries(groupedChannels).forEach(([channel, values]) => {
        if (values.length < 2) return;
        
        const firstValue = values[0];
        const lastValue = values[values.length - 1];
        
        if (firstValue === 0) return; // Avoid division by zero
        
        const growthPercent = ((lastValue - firstValue) / firstValue) * 100;
        
        if (growthPercent > maxGrowth) {
          maxGrowth = growthPercent;
          topGrowthChannel = {
            channel,
            growthPercent: Math.round(growthPercent * 100) / 100,
            from: firstValue,
            to: lastValue,
            fromPeriod: periods[0],
            toPeriod: periods[periods.length - 1],
          };
        }
      });

      return topGrowthChannel;
    };
    
    // Find top performers
    const topChannel = groupedStats[0] || null;
    const mostGrowth = calculateMostGrowth(groupedChannels, periods);

    return {
      chartData,
      channelStats: groupedStats,
      totals,
      metadata,
      topPerformers: {
        topChannel,
        mostGrowth,
      },
      originalChannelStats: channel_stats, // Keep original for reference
    };
  }, [apiData]);

  return {
    ...transformedData,
    loading: false, // Mock never loads
    refreshing: isRefetching,
    error: null, // Mock never errors
    isError: false,
    initialized: true, // Mock is always initialized
    refetch,
    lastUpdated: dataUpdatedAt,
  };
};